/** Web Push registration and subscription.
 *
 *  Replaces `cleanup-sw.ts`. That module registered a janitor whose job was to
 *  leave this origin with **no** service worker, after a stale Memso PWA worker
 *  hijacked navigation and replayed a cached shell. The janitor did its job;
 *  `public/sw.js` is now a push-only worker with no `fetch` handler, which
 *  cannot reproduce that failure — see the long comment at the top of that file
 *  before changing any of this.
 *
 *  Everything here is best-effort. Push is a nice-to-have layered on a mail
 *  client that must keep working without it, so no failure in this module is
 *  allowed to surface to the user or block boot.
 */

import { csrfFetch } from "./auth.js";

const SW_URL = "/sw.js";

export type PushState = {
  /** The browser can do push at all (not Safari-on-iOS-without-install, etc). */
  supported: boolean;
  /** Notification.permission, or "unsupported". */
  permission: NotificationPermission | "unsupported";
  subscribed: boolean;
};

/** VAPID keys travel as base64url; PushManager wants raw bytes.
 *  Returns an ArrayBuffer rather than a Uint8Array because lib.dom's
 *  BufferSource does not accept a Uint8Array over a generic ArrayBufferLike. */
function base64UrlToBytes(base64Url: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i);
  return buffer;
}

export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Register the worker. Safe to call on every boot — the browser dedupes by
 *  script URL and only re-installs when the file changed. */
export async function registerPushServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  try {
    return await navigator.serviceWorker.register(SW_URL, { scope: "/" });
  } catch {
    return null;
  }
}

export async function getPushState(): Promise<PushState> {
  if (!pushSupported()) return { supported: false, permission: "unsupported", subscribed: false };
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    subscribed = !!(await reg?.pushManager.getSubscription());
  } catch {
    /* treat as not subscribed */
  }
  return { supported: true, permission: Notification.permission, subscribed };
}

/** Ask for permission and subscribe.
 *
 *  Must be called from a user gesture: browsers reject a permission prompt
 *  that was not obviously requested, and a silently-denied prompt is worse
 *  than not asking — the user never sees it and can never be asked again. */
export async function enablePush(): Promise<{ ok: boolean; reason?: string }> {
  if (!pushSupported()) return { ok: false, reason: "This browser cannot do push notifications." };

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return {
      ok: false,
      reason:
        permission === "denied"
          ? "Notifications are blocked for this site. Enable them in the browser's site settings."
          : "Notification permission was not granted.",
    };
  }

  const reg = (await registerPushServiceWorker()) ?? (await navigator.serviceWorker.ready);
  if (!reg) return { ok: false, reason: "The notification worker did not register." };

  // The server owns the VAPID keypair; the client only ever sees the public
  // half, so a stolen bundle cannot send pushes.
  let publicKey = "";
  try {
    const res = await fetch("/api/push/key", { credentials: "same-origin" });
    const body = (await res.json()) as { ok?: boolean; publicKey?: string };
    publicKey = body?.publicKey ?? "";
  } catch {
    /* fall through */
  }
  if (!publicKey) return { ok: false, reason: "The server has no push key configured yet." };

  let sub: PushSubscription;
  try {
    sub = await reg.pushManager.subscribe({
      // Chrome requires this; a subscription that could deliver silently is
      // not permitted, which is fine — every push here shows a notification.
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(publicKey),
    });
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : "Could not subscribe to push." };
  }

  const r = await csrfFetch("/api/push/subscribe", {
    method: "POST",
    body: JSON.stringify({
      subscription: sub.toJSON(),
      userAgent: navigator.userAgent.slice(0, 200),
    }),
  });
  if (!r.ok) return { ok: false, reason: `The server rejected the subscription (HTTP ${r.status}).` };
  return { ok: true };
}

export async function disablePush(): Promise<void> {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    // Tell the server first: a subscription it keeps pushing to after the
    // browser dropped it is how you end up with a permanently failing queue.
    await csrfFetch("/api/push/unsubscribe", {
      method: "POST",
      body: JSON.stringify({ endpoint: sub.endpoint }),
    }).catch(() => null);
    await sub.unsubscribe();
  } catch {
    /* best effort */
  }
}
